import apiClient from './client';
import type { Conversation, ConversationSummary } from '@/types/conversation';
import type { PaginatedItemsResponse, ItemsQueryParams } from '@/types/api';

const API_VERSION = 'v2';

/**
 * Get all conversations for the authenticated user
 *
 * v2: GET /openai/v2/conversations -> { conversations: [...] }
 */
export async function getConversations(): Promise<ConversationSummary[]> {
  const response = await apiClient.get(`/openai/${API_VERSION}/conversations`);
  // v2 API returns { conversations: [...] }
  return response.data.conversations;
}

/**
 * Get a single conversation metadata (no messages in v2)
 *
 * v2: GET /openai/v2/conversations/:id -> metadata only
 */
export async function getConversation(id: string): Promise<Conversation> {
  const response = await apiClient.get(
    `/openai/${API_VERSION}/conversations/${id}`
  );
  return response.data;
}

/**
 * Get paginated messages for a conversation
 *
 * v2: GET /openai/v2/conversations/:id/items
 */
export async function getConversationItems(
  conversationId: string,
  params: ItemsQueryParams = {}
): Promise<PaginatedItemsResponse> {
  const { limit = 20, after, order = 'asc' } = params;

  const queryParams = new URLSearchParams();
  queryParams.set('limit', String(limit));
  queryParams.set('order', order);
  if (after) queryParams.set('after', after);

  const response = await apiClient.get(
    `/openai/${API_VERSION}/conversations/${conversationId}/items?${queryParams}`
  );
  return response.data;
}

/**
 * Delete a conversation
 *
 * v2: DELETE /openai/v2/conversations/:id
 */
export async function deleteConversation(id: string): Promise<void> {
  await apiClient.delete(`/openai/${API_VERSION}/conversations/${id}`);
}
